import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

// Blog posts data
export const blogPosts = [
  {
    id: 'how-to-pass-driving-test-first-time-liverpool',
    title: 'How to Pass Your Driving Test First Time in Liverpool?',
    excerpt: 'Passing your driving test on the first attempt is something every learner driver hopes for. Here are essential tips to help increase your chances of passing first time.',
    image: 'https://customer-assets.emergentagent.com/job_dd9d9ce7-fb86-432d-9760-9b14a54b987b/artifacts/aby7covz_IMG_1113.jpeg',
    author: 'Top Driver Team',
    date: '2025-02-28',
    readTime: '8 min read',
    category: 'Driving Tips',
    tags: ['driving test', 'liverpool', 'pass first time', 'learner driver', 'driving tips']
  },
  {
    id: 'automatic-vs-manual-which-should-you-learn-liverpool',
    title: 'Automatic vs Manual: Which Should You Learn in Liverpool?',
    excerpt: 'Choosing between automatic and manual driving lessons is one of the first decisions learner drivers need to make. This guide explains the key differences to help you decide.',
    image: 'https://images.unsplash.com/photo-1769254872789-57a420178849?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxhdXRvbWF0aWMlMjBjYXIlMjBnZWFyYm94JTIwdHJhbnNtaXNzaW9ufGVufDB8fHx8MTc3NDUxNjA0OXww&ixlib=rb-4.1.0&q=85',
    author: 'Top Driver Team',
    date: '2025-03-26',
    readTime: '7 min read',
    category: 'Driving Guide',
    tags: ['automatic lessons', 'manual lessons', 'liverpool', 'learner driver', 'automatic vs manual', 'driving lessons']
  },
  {
    id: 'what-to-expect-first-driving-lesson',
    title: 'What to Expect on Your First Driving Lesson',
    excerpt: 'Feeling nervous about your first driving lesson? Here\'s everything you need to know about what happens during your first lesson and how to prepare.',
    image: 'https://images.pexels.com/photos/6817037/pexels-photo-6817037.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    author: 'Top Driver Team',
    date: '2025-03-26',
    readTime: '6 min read',
    category: 'Beginner Guide',
    tags: ['first driving lesson', 'beginner driver', 'liverpool', 'driving lessons', 'learner tips']
  },
  {
    id: 'liverpool-driving-test-routes-guide',
    title: 'Liverpool Driving Test Routes: What You Need to Know',
    excerpt: 'Preparing for your driving test in Liverpool? Learn about the common test routes, challenging areas, and tips to help you feel confident on test day.',
    image: 'https://images.unsplash.com/photo-1638538008489-30c222c1c507?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxMaXZlcnBvb2wlMjBjaXR5JTIwcm9hZCUyMHN0cmVldCUyMGRyaXZpbmd8ZW58MHx8fHwxNzc0NTE2MzY4fDA&ixlib=rb-4.1.0&q=85',
    author: 'Top Driver Team',
    date: '2025-03-26',
    readTime: '7 min read',
    category: 'Test Preparation',
    tags: ['driving test routes', 'liverpool test centre', 'norris green', 'garston', 'test preparation']
  },
  {
    id: 'how-many-driving-lessons-to-pass-uk',
    title: 'How Many Driving Lessons Do I Need to Pass in the UK?',
    excerpt: 'The average learner needs around 45 hours of professional lessons plus 20 hours of private practice. Learn what factors affect how many lessons you\'ll need.',
    image: 'https://images.unsplash.com/photo-1570696657911-287e234bb781?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxkcml2aW5nJTIwbGVzc29uJTIwbGVhcm5lciUyMHN0dWRlbnQlMjBjYXJ8ZW58MHx8fHwxNzc1NDk4NDY0fDA&ixlib=rb-4.1.0&q=85',
    author: 'Top Driver Team',
    date: '2025-04-06',
    readTime: '8 min read',
    category: 'Learner Guide',
    tags: ['driving lessons', 'how many lessons', 'learner driver', 'automatic lessons', 'pass driving test', 'UK driving']
  },
  {
    id: 'how-to-choose-driving-instructor-liverpool',
    title: 'How to Choose a Driving Instructor in Liverpool: Complete Guide',
    excerpt: 'Finding the right driving instructor can make all the difference to your learning experience. Here\'s what to look for when choosing a driving instructor in Liverpool.',
    image: 'https://customer-assets.emergentagent.com/job_2343b8d4-bf7c-477c-9f5a-ad0017c87460/artifacts/yg3mqisk_IMG_1100.jpeg',
    author: 'Top Driver Team',
    date: '2025-04-15',
    readTime: '9 min read',
    category: 'Choosing Instructor',
    tags: ['driving instructor liverpool', 'choose driving instructor', 'best driving school liverpool', 'DVSA approved instructor', 'automatic instructor liverpool']
  }
];

export default function Blog() {
  return (
    <>
      <SEO 
        title="Driving Tips & Blog | Top Driver Driving School Liverpool"
        description="Expert driving tips, advice for learner drivers, and guides to help you pass your driving test first time. Read our blog for the latest driving tips in Liverpool."
        keywords="driving tips, learner driver advice, pass driving test, driving lessons blog, liverpool driving school blog"
        canonical="https://topdriver.co.uk/blog"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-black mb-6">
            Driving Tips & <span className="text-red-600">Blog</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert advice, tips, and guides to help you become a confident driver and pass your test first time.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
                <Link to={`/blog/${post.id}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {post.category}
                      </span>
                    </div>
                  </div>
                </Link>
                <CardContent className="p-6">
                  <Link to={`/blog/${post.id}`}>
                    <h2 className="text-xl font-bold text-black mb-3 group-hover:text-red-600 transition-colors">
                      {post.title}
                    </h2>
                  </Link>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {post.readTime}
                      </span>
                    </div>
                  </div>
                  <Link to={`/blog/${post.id}`}>
                    <Button variant="link" className="text-red-600 hover:text-red-700 p-0 mt-4">
                      Read More <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* More articles coming soon */}
          {blogPosts.length < 3 && (
            <div className="text-center mt-12 p-8 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-700 mb-2">More articles coming soon!</h3>
              <p className="text-gray-600">We're working on more helpful guides and tips for learner drivers.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to start your driving journey?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Book your first lesson with Top Driver today
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white hover:bg-gray-100 text-red-600 px-8 py-6 text-lg font-semibold"
          >
            <Link to="/contact">Book a Lesson</Link>
          </Button>
        </div>
      </section>
    </>
  );
}
